import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { Spinner } from "@/shared/components/spinner";
import { EmptyState } from "@/shared/components/EmptyState";
import { Pagination } from "@/shared/components/Pagination";
import { cn } from "@/shared/lib/utils";
import {
  getDocumentPages,
  DocumentApiError,
  type DocumentPagesResponse,
  type DocumentPage,
  type DocumentPageStatus,
} from "@/features/patient/records/api/documentPagesApi";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";

const PAGE_SIZE = 5;
// "A few seconds" per the extraction-progress UX — long documents (hundreds of
// pages) can take minutes end-to-end, so this only needs to feel responsive,
// not real-time.
const POLL_INTERVAL_MS = 4000;
// Without a cap, a backend job that never reaches a terminal status (worker
// crash, stuck extraction) leaves this polling forever with no indication
// anything is wrong. 5 minutes comfortably covers a large multi-page document.
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
// Below this length, a page's text is shown in full with no expand control.
const COLLAPSE_THRESHOLD = 320;

const PAGE_STATUS_META: Record<DocumentPageStatus, { label: string; icon: string; badgeClass: string }> = {
  extracted: { label: "Extracted", icon: "lucide:check-circle-2", badgeClass: "bg-[#F0FDF4] text-[#16A34A]" },
  needs_review: { label: "Needs review", icon: "lucide:alert-triangle", badgeClass: "bg-[#FFFBEB] text-[#B45309]" },
  failed: { label: "Couldn't be read", icon: "lucide:alert-circle", badgeClass: "bg-[#FEF2F2] text-[#DC2626]" },
};

/** Translates a 0-100 confidence score into plain language — thresholds mirror the
 *  backend's own review cutoff (CONFIDENCE_REVIEW_THRESHOLD = 80). */
function confidenceLabel(confidence: number | null): { text: string; tone: "good" | "warn" | "bad" } | null {
  if (confidence === null) return null;
  if (confidence >= 80) return { text: "High confidence", tone: "good" };
  if (confidence >= 50) return { text: "Medium confidence — some words may be unclear", tone: "warn" };
  return { text: "Low confidence — may contain errors", tone: "bad" };
}

const CONFIDENCE_TONE_CLASS: Record<"good" | "warn" | "bad", string> = {
  good: "text-[#16A34A]",
  warn: "text-[#B45309]",
  bad: "text-[#DC2626]",
};

function PageTextBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = text.length > COLLAPSE_THRESHOLD;
  const preview = needsCollapse ? `${text.slice(0, COLLAPSE_THRESHOLD).trimEnd()}…` : text;

  return (
    <div>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#374151]">
        {expanded || !needsCollapse ? text : preview}
      </p>
      {needsCollapse && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="!mt-2 !h-auto !w-full !justify-center !rounded-lg !p-2 !text-xs !font-semibold !text-[#4B5563] hover:!bg-[#FFF0F3] hover:!text-[#E4002B]"
        >
          <Icon icon={expanded ? "lucide:chevron-up" : "lucide:chevron-down"} className="mr-1.5 h-3.5 w-3.5" />
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}

function PageRow({ page }: { page: DocumentPage }) {
  const meta = PAGE_STATUS_META[page.status];
  const confidence = confidenceLabel(page.confidence);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold text-[#0A2947]">Page {page.pageNumber}</p>
        <span className={cn("flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold", meta.badgeClass)}>
          <Icon icon={meta.icon} className="h-3 w-3" />
          {meta.label}
        </span>
      </div>

      {confidence && (
        <p className={cn("mt-1.5 text-xs font-semibold", CONFIDENCE_TONE_CLASS[confidence.tone])}>
          {confidence.text}
        </p>
      )}

      <div className="mt-3">
        {page.status === "failed" ? (
          <p className="flex items-center gap-2 rounded-xl bg-[#FEF2F2] px-3 py-2.5 text-xs font-semibold text-[#DC2626]">
            <Icon icon="lucide:alert-circle" className="h-4 w-4 shrink-0" />
            This page couldn't be read. It may be blank, badly scanned, or in a format we can't process.
          </p>
        ) : page.extractedText ? (
          <>
            {page.status === "needs_review" && (
              <p className="mb-2 flex items-center gap-2 rounded-xl bg-[#FFFBEB] px-3 py-2.5 text-xs font-semibold text-[#B45309]">
                <Icon icon="lucide:alert-triangle" className="h-4 w-4 shrink-0" />
                This text may contain reading errors — double-check anything important.
              </p>
            )}
            <PageTextBlock text={page.extractedText} />
          </>
        ) : (
          <p className="text-xs font-medium text-[#9CA3AF]">No text was found on this page.</p>
        )}
      </div>
    </div>
  );
}

/** Inline (non-modal) "Extracted Text" view — replaces the record-cards area of
 *  UploadDetailPanel while active. Ported from the old DocumentTextViewer modal:
 *  same polling/pagination behavior, just hosted directly on the page instead of
 *  behind its own dialog, per the new full-page upload-history design. */
export function ExtractedTextPanel({
  documentReferenceId,
  actingAsProfileId,
}: {
  documentReferenceId: string;
  actingAsProfileId?: string;
}) {
  const [page, setPage] = useState(1);
  const [pagesResponse, setPagesResponse] = useState<DocumentPagesResponse | null>(null);
  const [loadError, setLoadError] = useState<DocumentApiError | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const { getCachedDocumentPages, cacheDocumentPages } = useUploadHistory();

  useEffect(() => {
    setPage(1);
  }, [documentReferenceId]);

  useEffect(() => {
    const guard = { cancelled: false, paused: false, timeoutId: undefined as number | undefined };
    const pollStartedAt = Date.now();

    const runFetch = async () => {
      try {
        const result = await getDocumentPages(documentReferenceId, {
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          actingAsProfileId,
        });
        if (guard.cancelled) return;
        setPagesResponse(result);
        cacheDocumentPages(documentReferenceId, page, result);
        setLoadError(null);
        if (result.status === "in_progress" || result.status === "not_started") {
          scheduleNext();
        } else {
          setPollTimedOut(false);
        }
      } catch (err) {
        if (guard.cancelled) return;
        setLoadError(err instanceof DocumentApiError ? err : new DocumentApiError(0, "Unable to load this document."));
      }
    };

    const scheduleNext = () => {
      if (guard.cancelled) return;
      if (Date.now() - pollStartedAt >= MAX_POLL_DURATION_MS) {
        setPollTimedOut(true);
        return;
      }
      if (document.visibilityState === "hidden") {
        guard.paused = true;
        return;
      }
      guard.timeoutId = window.setTimeout(runFetch, POLL_INTERVAL_MS);
    };

    const onVisibilityChange = () => {
      if (guard.cancelled || !guard.paused) return;
      if (document.visibilityState === "visible") {
        guard.paused = false;
        runFetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    setLoadError(null);
    setPollTimedOut(false);

    // In-memory-only cache (see UploadHistoryContext — never localStorage,
    // this is PHI): a (document, page) pair already seen this session
    // redisplays instantly instead of blanking to a spinner. A cached page
    // that was already "complete"/"failed" needs no further fetch at all; one
    // still "in_progress"/"not_started" shows what's already known while a
    // fresh poll picks up right where it left off, same as a first visit.
    const cached = getCachedDocumentPages(documentReferenceId, page);
    if (cached) {
      setPagesResponse(cached);
      if (cached.status === "in_progress" || cached.status === "not_started") {
        runFetch();
      }
    } else {
      setPagesResponse(null);
      runFetch();
    }

    return () => {
      guard.cancelled = true;
      if (guard.timeoutId) window.clearTimeout(guard.timeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [documentReferenceId, page, actingAsProfileId, retryToken, getCachedDocumentPages, cacheDocumentPages]);

  const retryLoad = () => setRetryToken((t) => t + 1);

  const isNotFound = loadError?.status === 404;
  const status = pagesResponse?.status;
  const isProcessing = status === "in_progress" || status === "not_started";
  const isFailed = status === "failed";
  const totalPages = pagesResponse?.totalPages ?? null;
  const processedCount = pagesResponse ? pagesResponse.extractedPages + pagesResponse.failedPages : 0;
  const isEmpty = status === "complete" && (totalPages ?? 0) === 0;
  const paginationTotal = totalPages ?? pagesResponse?.count ?? 0;

  return (
    <div>
      {loadError && !isNotFound && (
        <EmptyState
          icon={<Icon icon="lucide:wifi-off" className="h-8 w-8 text-[#9CA3AF]" />}
          title="We couldn't load this document"
          description="Check your connection and try again."
          action={
            <Button variant="outline" onClick={retryLoad} className="!h-9 !rounded-full !border-[#E5E7EB] !px-4 !text-xs !font-bold">
              Try again
            </Button>
          }
        />
      )}

      {loadError && isNotFound && (
        <EmptyState
          icon={<Icon icon="lucide:file-question" className="h-8 w-8 text-[#9CA3AF]" />}
          title="This document isn't available"
          description="It may have been removed, or you may not have access to it."
        />
      )}

      {!loadError && !pagesResponse && (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-[#8A8A8A]">
          <Spinner />
          Loading…
        </div>
      )}

      {!loadError && pagesResponse && (
        <>
          {isProcessing && pollTimedOut && (
            <EmptyState
              icon={<Icon icon="lucide:clock-alert" className="h-8 w-8 text-[#9CA3AF]" />}
              title="This is taking longer than expected"
              description={
                totalPages != null
                  ? `Still processing — ${processedCount} of ${totalPages} pages complete. Check back in a few minutes.`
                  : "We're still working on this document. Check back in a few minutes."
              }
              action={
                <Button variant="outline" onClick={retryLoad} className="!h-9 !rounded-full !border-[#E5E7EB] !px-4 !text-xs !font-bold">
                  Check again
                </Button>
              }
            />
          )}

          {isProcessing && !pollTimedOut && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#F7B4C3] bg-[#FFF7F8] px-4 py-3">
              <Icon icon="lucide:loader-2" className="h-5 w-5 shrink-0 animate-spin text-[#F17893]" />
              <p className="text-sm font-semibold text-[#0A2947]">
                {totalPages != null ? `Processing — ${processedCount} of ${totalPages} pages complete` : "Processing your document…"}
              </p>
            </div>
          )}

          {isFailed && (
            <EmptyState
              icon={<Icon icon="lucide:alert-circle" className="h-8 w-8 text-[#DC2626]" />}
              title="This document could not be processed"
              description="We weren't able to read this file. You can try uploading it again."
            />
          )}

          {!isFailed && isEmpty && (
            <EmptyState
              icon={<Icon icon="lucide:file-x" className="h-8 w-8 text-[#9CA3AF]" />}
              title="No readable text was found"
              description="We couldn't find any text in this document."
            />
          )}

          {!isFailed && !isEmpty && pagesResponse.pages.length > 0 && (
            <div className="space-y-3">
              {pagesResponse.pages.map((p) => (
                <PageRow key={p.pageNumber} page={p} />
              ))}
            </div>
          )}

          {!isFailed && !isEmpty && paginationTotal > PAGE_SIZE && (
            <div className="mt-5">
              <Pagination page={page} limit={PAGE_SIZE} total={paginationTotal} onChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
